import { DataProvider } from 'react-admin';
import { BSON } from 'realm-web';
import { therapistsCustomFetcher } from '@/screens/therapists/therapistsCustomFetcher';
import { sessionNotesCustomFetcher } from '@/screens/therapistSessionNotes/sessionNotesCustomFetcher';
import { journalEntriesCustomFetcher } from '@/screens/journalEntries/journalEntriesCustomFetcher';
import { therapistPatientsCustomFetcher } from '@/screens/therapistPatients/therapistPatientsCustomFetcher';
import {
  aggregate,
  getOne,
  powersyncIngestionClient,
} from './backendClient';
import { therapistHomeworkCustomFetcher } from '@/screens/therapistHomework/therapistHomeworkCustomFetcher';
import { deadLetterQueueCustomFetcher } from '@/screens/deadLetterQueue/deadLetterQueueCustomFetcher';
import { appTrackerCustomFetcher } from '@/screens/appTrackerEvents/appTrackerCustomFetcher';
import { dataExportsCustomFetcher } from '@/screens/data-exports/dataExportsCustomFetcher';
import { conversionMonitorCustomFetcher } from '@/screens/conversionMonitor/conversionMonitorCustomFetcher';
import { reportsCustomFetcher } from '@/screens/reports/reportsCustomFetcher';

export type Sort = {
  field: string;
  order: 'ASC' | 'DESC';
};

export type MultiSort = Sort | Sort[];

export type GetListResult = {
  total: number;
  results: any[];
};

export type CustomPipelineOptions = {
  sort: Sort | Sort[];
  filter: Record<string, any>;
  page: number;
  perPage: number;
  projection?: string;
};

export type GetCustomPipelineOutput = {
  resultsPipeline: any[];
  countPipeline: any[];
};

export type CustomPipelineFactory = (
  options: CustomPipelineOptions,
) => Promise<GetListResult>;

export const dataProvider: DataProvider = {
  create: async (
    resource,
    { data: { id = new BSON.ObjectId().toHexString(), ...data } },
  ) => {
    const result = await powersyncIngestionClient(
      resource,
      'PUT',
      data,
      id,
    );

    console.log('CREATE RESULT', result);

    return {
      data: {
        id,
        ...data,
      } as any,
    };
  },
  getOne: async (resource, { id, meta }) => {
    // const collection = getCollection(resource);
    const { raw = false } = meta || {};
    const useRawId = raw || resource === 'documentComposerSkills';

    const result = await getOne(resource, id as any, useRawId);

    let data = result ? (convertMongoToRawObj(result) as any) : null;

    // So the Edit form gets promptEntries and ruleCategories from the start.
    if (data && resource === 'documentComposerRulesets') {
      // Handle potential results wrapper from API
      const doc = Array.isArray(data.results) && data.results.length > 0 ? data.results[0] : data;
      
      const analyzeSteps = doc.analyzeSteps ?? [];
      const seq = doc.operationSequence ?? [];
      const tmpl = doc.templates ?? {};
      const rules = doc.rules ?? [];

      // Group rules by skill (requirements | redactions). Option A: only id + rules per skill; no statuses/fieldRules.
      const order: string[] = [];
      const bySkill = new Map<string, any[]>();
      for (const r of rules) {
        const skill = r.skill ?? r.category ?? 'requirements';
        if (!bySkill.has(skill)) {
          order.push(skill);
          bySkill.set(skill, []);
        }
        bySkill.get(skill)!.push({
          id: r.id || '',
          fieldType: r.fieldType || 'text_area',
          description: r.description || '',
          agentPromptDescription: r.agentPromptDescription || '',
        });
      }

      const promptEntries = analyzeSteps.length > 0
        ? analyzeSteps.map((step: any) => ({
            skillIds: step.skillIds ?? step.outputCategoryIds ?? [],
            systemMessage: step.systemMessage ?? '',
            userPromptTemplate: step.userPromptTemplate ?? '',
            jsonSchema: step.jsonSchema ?? undefined,
          }))
        : seq.map((opId: string) => ({
            skillIds: [opId],
            systemMessage: tmpl[opId]?.systemMessage ?? '',
            userPromptTemplate: tmpl[opId]?.userPromptTemplate ?? '',
            jsonSchema: tmpl[opId]?.jsonSchema ?? undefined,
          }));

      const validOrder = order.filter((id) => id !== 'documentCreation');
      const transformedData = {
        ...doc,
        promptEntries,
        ruleCategories: validOrder.map((id) => ({
          id,
          rules: bySkill.get(id) || [],
        })),
        applyTemplate: {
          systemMessage: tmpl['documentCreation']?.systemMessage ?? tmpl['apply_changes']?.systemMessage ?? '',
          userPromptTemplate: tmpl['documentCreation']?.userPromptTemplate ?? tmpl['apply_changes']?.userPromptTemplate ?? '',
        },
      };

      // If we extracted from results, replace the whole data object
      data = transformedData;
    }

    return { data };
  },
  getMany: async (resource, { ids, meta }) => {
    const { raw = false } = meta || {};

    const usableIds = !raw
      ? ids.map(id => convertStringToObjectId(id as any, resource))
      : ids;

    const pipeline = [{ $match: { _id: { $in: usableIds } } }];

    const aggregation = {
      [resource]: pipeline,
    };

    const results = await aggregate(resource, aggregation);

    const extractedResults = (results as any)[resource];

    return { data: extractedResults.map(convertMongoToRawObj) };
  },
  getManyReference: async (resource, { target, id, sort, meta }) => {
    const { raw = false, projection, $addFields = true } = meta || {};

    // const collection = getCollection(resource);

    const usableId = !raw
      ? convertStringToObjectId(id as any, resource)
      : id;

    const filteringStage = createFilteringStage({
      [target]: usableId,
    });

    const sortingStage = createSortStage(sort);

    const projectionStage = projection
      ? createProjectionStage(projection, $addFields)
      : [];

    const dataPipeline = [
      filteringStage,
      sortingStage,
      ...projectionStage,
    ];

    const countPipeline = createCountPipeline([filteringStage]);

    const pipelines = { data: dataPipeline, total: countPipeline };

    const {
      data = [],
      total: [{ count = 0 }],
    } = (await aggregate(resource, pipelines)) as any;

    return {
      data: data.map(convertMongoToRawObj) as any,
      total: count,
    };
  },
  getList: async (
    resource,
    { sort, filter, meta, pagination: { page = 1, perPage = 20 } },
  ) => {
    console.debug('GET LIST', resource, sort, filter);

    const { results, total } = await (async () => {
      const {
        projection,
        $addFields = true,
        allowDiskUse = false,
        raw = false,
      } = meta || {};

      const customFetcher =
        customFetchers[resource as keyof typeof customFetchers];

      if (customFetcher) {
        const customResult = await customFetcher({
          sort,
          filter,
          page,
          perPage,
          projection,
        });
        // Custom fetchers return { results, total }
        // Ensure all results have an 'id' field (required by react-admin)
        const dataWithIds = (customResult.results || []).map((item: any) => {
          // If item already has id, use it; otherwise try _id or generate one
          if (!item.id) {
            if (item._id) {
              item.id = item._id;
            } else {
              // Generate a stable id from the item's properties
              item.id = item.path || item.name || JSON.stringify(item).slice(0, 24) || `item-${Math.random()}`;
            }
          }
          return item;
        });
        return { results: dataWithIds, total: customResult.total };
      }

      const { filteringStage, filteringAndPagingPipeline } =
        createDefaultPipeline(
          filter,
          sort,
          page,
          perPage,
          projection,
          $addFields,
        );

      const pipelines = {
        results: filteringAndPagingPipeline,
        total: createCountPipeline([filteringStage]),
      };

      const { results = [], total: [{ count = 0 } = {}] = [] } =
        (await aggregate(resource, pipelines, {
          allowDiskUse,
        })) as any;

      return { results, total: count || 0 } as const;
    })();

    // Ensure all results have an 'id' field (required by react-admin)
    const dataWithIds = (results || []).map((item: any) => {
      const converted = convertMongoToRawObj(item);
      // Ensure id exists - if not, try to use _id or generate one
      if (!converted.id && converted._id) {
        converted.id = converted._id;
      } else if (!converted.id) {
        // Last resort: generate an id from the item
        converted.id = JSON.stringify(item).slice(0, 24) || `item-${Math.random()}`;
      }
      return converted;
    });

    return {
      data: dataWithIds,
      total,
    } as any;
  },
  update: async (
    resource,
    { id, data: { id: overrideId, ...data }, previousData },
  ) => {
    const actualId = overrideId || id;
    await powersyncIngestionClient(
      resource,
      'PATCH',
      data,
      actualId,
    );

    return {
      data: {
        id: actualId, // Ensure id is always included
        ...previousData,
        ...data,
      },
    };
  },
  updateMany: async (resource, { ids, data, meta }) => {
    throw new Error('Not supported - dangerous operation');
  },
  delete: async (resource, { id, meta }) => {
    throw new Error('Not supported - dangerous operation');
  },
  deleteMany: async (resource, { ids, meta }) => {
    throw new Error('Not supported - dangerous operation');
  },
};

const convertMongoToRawObj = <T>({ _id, ...rest }: any) =>
  BSON.EJSON.deserialize({
    ...rest,
    id: _id,
  }) as T;

const convertRawObjToMongo = ({ id, ...rest }: any) => ({
  ...rest,
  ...(id ? { _id: id } : {}),
});

const convertStringToObjectId = (
  id: string | BSON.ObjectId,
  resource: string,
) => {
  if (objectIdConversionExceptions.has(resource)) {
    return id;
  }

  return typeof id === 'string' && id.length === 24
    ? new BSON.ObjectId(id)
    : id;
};

const objectIdConversionExceptions = new Set([
  'users',
  'therapists',
  'userMetadata',
  'documentComposerSkills',
]);

export const createFacetingPipeline = (
  resultsPipeline: any[],
  countPipeline = createCountPipeline(resultsPipeline),
) => {
  const facetingPipeline = [
    {
      $facet: {
        total: countPipeline,
        results: resultsPipeline,
      },
    },
    {
      $unwind: '$total',
    },
    {
      $addFields: {
        total: '$total.count',
      },
    },
  ];

  return facetingPipeline;
};

const createDefaultPipeline = (
  filter: any,
  sort: any,
  page: number,
  perPage: number,
  projection: any,
  $addFields: boolean,
) => {
  const filteringStage = createFilteringStage(filter);

  const filteringAndPagingPipeline = [
    filteringStage,
    createSortStage(sort),
    ...createPagingStages(page, perPage),
    ...createProjectionStage(projection, $addFields),
  ];

  return { filteringStage, filteringAndPagingPipeline };
};

export const createSortStage = (sort: Sort) => {
  const sortField = sort.field === 'id' ? '_id' : sort.field;
  const sortOrder = sort.order === 'ASC' ? 1 : -1;

  return { $sort: { [sortField]: sortOrder } };
};

export const createPagingStages = (page: number, perPage: number) => [
  { $skip: (page - 1) * perPage },
  { $limit: perPage },
];

export const createProjectionStage = (
  projection: any,
  $addFields: boolean,
) =>
  projection
    ? [{ [$addFields ? '$addFields' : '$project']: projection }]
    : [];

export const createFilteringStage = (filter: any) => {
  // Handle _includeDeleted special parameter
  const { _includeDeleted, ...restFilter } = filter || {};
  const filterObj = replaceRawFilters(restFilter);

  // If _includeDeleted is true, ensure we don't exclude items with deletedAt
  // By removing any deletedAt filter, we allow both active and archived items to be returned
  if (_includeDeleted) {
    // Remove any deletedAt filter that might exclude deleted items
    delete filterObj.deletedAt;
    // Note: If the backend has default filtering that excludes deletedAt, 
    // that would need to be handled at the backend level
  }

  return { $match: filterObj };
};

export const replaceRawFilters = (filter: any) =>
  Object.fromEntries(
    Object.entries(filter).map(([key, value]) => {
      const replacedKey =
        key.includes('$') && !key.startsWith('$')
          ? key.replace(/[$]/g, '.')
          : key;

      const replacedValue =
        value && typeof value === 'object' && '$$raw' in value
          ? JSON.parse(value.$$raw as string)
          : value;

      if (replacedKey !== key) {
        console.log('REPLACED KEY', replacedKey, key);
      }

      if (value && typeof value === 'object' && '$$raw' in value) {
        return [replacedKey, JSON.parse(value.$$raw as string)];
      }

      return [replacedKey, value];
    }),
  );

export const createCountPipeline = (filteringStage: any[]) => [
  ...(Array.isArray(filteringStage)
    ? filteringStage
    : [filteringStage]),
  {
    $project: {
      _id: 1,
    },
  },
  {
    $count: 'count',
  },
];

export const getAggregationResult = async (
  resource: string,
  resultsPipeline: any[],
) => {
  const aggregationResult = await aggregate(
    resource,
    resultsPipeline,
  );

  return aggregationResult?.[resource];
};

export const getAggregationCount = async (
  resource: string,
  countPipeline: any[],
) => {
  const aggregationResult = await aggregate(resource, countPipeline);

  return aggregationResult?.[resource]?.[0]?.count || 0;
};

export const runParallelQueries = async (
  resource: string,
  resultsPipeline: any[],
  countFilter: any,
) => {
  const pipelines = {
    results: resultsPipeline,
    total: createCountPipeline(countFilter),
  };

  const {
    results,
    total: [{ count = 0 } = {}],
  } = (await aggregate(resource, pipelines)) as any;

  return { results, total: count || 0 };
};

export const customFetchers = {
  therapists: therapistsCustomFetcher,
  appTrackerEvents: appTrackerCustomFetcher,
  dataExports: dataExportsCustomFetcher,
  therapistSessionNotes: sessionNotesCustomFetcher,
  therapistHomework: therapistHomeworkCustomFetcher,
  vw_admin_journalEntries: journalEntriesCustomFetcher,
  therapistPatients: therapistPatientsCustomFetcher,
  deadLetterQueue: deadLetterQueueCustomFetcher,
  conversionMonitor: conversionMonitorCustomFetcher,
  reports: reportsCustomFetcher,
} as const;
